import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import i18n from '../../i18n'
import useAppStore from '../../store/useAppStore'
import crashSocket from '../crash/services/crashSocket'
import crashApi from '../crash/services/crashApi'
import apiService from '../../services/api'
import useAudioManager from '../../hooks/useAudioManager'
import { hapticNotify } from '../../utils/haptic'
import useUpdateBlocker from '../../hooks/useUpdateBlocker'
import { prepareUpdateRecovery } from '../../services/appUpdateService'

const emptyLimits = { min_bet: 100, max_bet: 100000, max_items: 10, idle_timeout: 300 }
const uidPattern = /^[0-9a-f]{32}$/i
const retainedGames = new Map()

export default function useMinesGame(isGuest) {
  const { playSound } = useAudioManager({ start: 'minesGameStart', safe: 'minesSafeClick', detonate: 'minesDetonate', cashout: 'minesCashout' })
  const [state, setState] = useState({ ready: false, connected: false, disabled: false, limits: emptyLimits, multipliers: {}, game: null, result: null, feed: [], inventory: [], inventoryReady: false, busy: false, clockOffset: 0 })
  const user = useAppStore.getState().user
  const userId = String(user?.telegramid ?? user?.chat_id ?? user?.id ?? '')
  const retained = !isGuest && retainedGames.get(userId)
  const gameRef = useRef(retained?.game || null)
  const pendingRef = useRef(retained?.pending || null)
  const actionRef = useRef(retained?.action || null)
  const stateRef = useRef(state)
  const betTimerRef = useRef(null)
  const actionTimerRef = useRef(null)
  const recoveryRef = useRef(null)
  const recoveryTimerRef = useRef(null)
  stateRef.current = state
  const hasPersonalGame = () => !isGuest && Boolean(stateRef.current.busy
    || gameRef.current || pendingRef.current || actionRef.current || recoveryRef.current)
  useUpdateBlocker(() => !isGuest && (!stateRef.current.ready || hasPersonalGame()), 'mines', hasPersonalGame)

  const scheduleBetRecovery = useCallback((clientId) => {
    clearTimeout(betTimerRef.current)
    betTimerRef.current = setTimeout(() => {
      if (pendingRef.current !== clientId) return
      pendingRef.current = null
      setState((prev) => ({ ...prev, busy: false, ready: false }))
      toast.error(i18n.t('mines.unconfirmed'))
      crashSocket.emit('mines:state')
    }, 3000)
  }, [])

  const refreshInventory = useCallback(async () => {
    try {
      const data = await crashApi.getInventory(undefined, true)
      setState((s) => ({ ...s, inventory: Array.isArray(data.inventory) ? data.inventory : [], inventoryReady: true }))
    } catch (error) {
      setState((s) => ({ ...s, inventoryReady: false }))
    }
  }, [])

  useEffect(() => {
    let active = true
    apiService.makeRequest('/api/mines/', { method: 'GET', skipBalanceSync: true }).then((data) => {
      if (!active) return
      setState((s) => s.ready ? s : ({ ...s, limits: data.limits || s.limits, multipliers: data.multipliers || s.multipliers, disabled: data.enabled === false }))
      if (data.game && !gameRef.current && !stateRef.current.ready) {
        gameRef.current = data.game
        setState((s) => ({ ...s, game: data.game }))
      }
    }).catch(() => { if (active) toast.error(i18n.t('mines.loadError')) })
    if (!isGuest) refreshInventory()
    return () => { active = false }
  }, [isGuest, refreshInventory])

  useEffect(() => {
    if (!state.connected || state.ready) return undefined
    const timer = setInterval(() => crashSocket.emit('mines:state'), 3000)
    return () => clearInterval(timer)
  }, [state.connected, state.ready])

  useEffect(() => {
    let active = true
    const unlock = () => {
      clearTimeout(actionTimerRef.current)
      actionRef.current = null
      setState((s) => ({ ...s, busy: false }))
    }
    const snapshot = async (data) => {
      if (!active) return
      const recoverUpdate = prepareUpdateRecovery('mines')
      if (Number.isFinite(Number(data.server_time))) setState((s) => ({ ...s, clockOffset: Number(data.server_time) - Date.now() }))
      setState((s) => ({ ...s, ready: data.ready === true, disabled: Boolean(data.disabled), limits: data.limits || s.limits, multipliers: data.multipliers || s.multipliers, feed: Array.isArray(data.feed) ? data.feed : s.feed }))
      if (data.ready !== true) return
      const previous = gameRef.current
      if (data.game && stateRef.current.result?.uid === data.game.uid) {
        clearTimeout(recoveryTimerRef.current)
        recoveryTimerRef.current = setTimeout(() => crashSocket.emit('mines:state'), 3000)
        return
      }
      if (data.game) {
        recoveryRef.current = null
        clearTimeout(recoveryTimerRef.current)
        gameRef.current = data.game
        clearTimeout(betTimerRef.current)
        pendingRef.current = null
        const pendingAction = actionRef.current
        const retryClick = pendingAction?.cell != null && pendingAction.uid === data.game.uid && pendingAction.step === data.game.step + 1
        const waitingForResult = pendingAction?.uid === data.game.uid && (pendingAction.cashout || pendingAction.terminal)
        if (retryClick) {
          crashSocket.emit('mines:click', { uid: data.game.uid, step: pendingAction.step, cell: pendingAction.cell })
          clearTimeout(actionTimerRef.current)
          actionTimerRef.current = setTimeout(() => crashSocket.emit('mines:state'), 3000)
        } else if (waitingForResult && pendingAction.cashout && !data.game.finishing) {
          crashSocket.emit('mines:cashout', { uid: data.game.uid })
          clearTimeout(actionTimerRef.current)
          actionTimerRef.current = setTimeout(() => crashSocket.emit('mines:state'), 3000)
        } else if (waitingForResult || data.game.finishing || data.game.step > 0 && !data.game.next_multiplier) {
          clearTimeout(actionTimerRef.current)
          actionTimerRef.current = setTimeout(() => crashSocket.emit('mines:state'), 3000)
        } else unlock()
        setState((s) => ({ ...s, game: data.game, result: null, busy: Boolean(retryClick || waitingForResult || data.game.finishing || data.game.step > 0 && !data.game.next_multiplier) }))
      } else if (previous?.uid && uidPattern.test(previous.uid)) {
        if (recoveryRef.current === previous.uid) return
        recoveryRef.current = previous.uid
        setState((s) => ({ ...s, busy: true }))
        try {
          const response = await apiService.makeRequest(`/api/mines/history/${previous.uid}/`, { method: 'GET', skipBalanceSync: true })
          if (active && recoveryRef.current === previous.uid && gameRef.current?.uid === previous.uid && response.game?.uid === previous.uid) {
            recoveryRef.current = null
            gameRef.current = null
            unlock()
            recoverUpdate()
            setState((s) => ({ ...s, game: null, result: response.game }))
          }
        } catch (error) {
          if (active && recoveryRef.current === previous.uid) {
            if (error?.status !== 404 && !error?.isHandledAuthError && !error?.isNotified) toast.error(i18n.t('mines.historyError'))
            clearTimeout(recoveryTimerRef.current)
            recoveryTimerRef.current = setTimeout(() => { recoveryRef.current = null; crashSocket.emit('mines:state') }, 3000)
          }
        }
      } else if (!pendingRef.current) {
        unlock()
        recoverUpdate()
        setState((s) => ({ ...s, game: null }))
      }
    }
    const onBet = ({ client_id, game }) => {
      if (!active || !game || (pendingRef.current && client_id !== pendingRef.current) || stateRef.current.result?.uid === game.uid) return
      if (!pendingRef.current && gameRef.current?.uid === game.uid) return
      if (gameRef.current && gameRef.current.uid !== game.uid) { crashSocket.emit('mines:state'); return }
      clearTimeout(betTimerRef.current)
      clearTimeout(recoveryTimerRef.current)
      recoveryRef.current = null
      pendingRef.current = null
      gameRef.current = game
      setState((s) => ({ ...s, game, result: null, busy: false }))
      playSound('start')
      hapticNotify('success')
    }
    const onClick = (data) => {
      const game = gameRef.current
      if (!active || !game || data.uid !== game.uid || data.step !== game.step + 1) return
      const next = { ...game, step: data.step, opened: [...(game.opened || []), data.cell], multiplier: data.multiplier, next_multiplier: data.next_multiplier, cashout: data.cashout, timeout_at: data.timeout_at }
      gameRef.current = next
      playSound('safe')
      if (data.next_multiplier === 0) {
        actionRef.current = { uid: data.uid, step: data.step, cell: data.cell, terminal: true }
        clearTimeout(actionTimerRef.current)
        actionTimerRef.current = setTimeout(() => crashSocket.emit('mines:state'), 3000)
        setState((s) => ({ ...s, game: next, busy: true }))
      } else {
        unlock()
        setState((s) => ({ ...s, game: next }))
      }
    }
    const onResult = (data) => {
      if (!active || !gameRef.current || data.uid !== gameRef.current.uid) return
      recoveryRef.current = null
      clearTimeout(recoveryTimerRef.current)
      gameRef.current = null
      unlock()
      prepareUpdateRecovery('mines')()
      setState((s) => ({ ...s, game: null, result: data }))
      if (data.result === 'lost') playSound('detonate')
      if (data.result === 'won') playSound('cashout')
      hapticNotify(data.result === 'lost' ? 'error' : 'success')
    }
    const onAlert = (data) => {
      if (!active || data.game !== 'mines') return
      if (data.client_id && data.client_id !== pendingRef.current) return
      if (data.uid && data.uid !== gameRef.current?.uid) return
      if (data.code === 'bet_unconfirmed' || data.code === 'bet_pending') {
        crashSocket.emit('mines:state')
        return
      }
      if (data.client_id) { clearTimeout(betTimerRef.current); pendingRef.current = null }
      if (data.uid) unlock()
      if (['game_active', 'bad_step', 'game_not_found', 'bad_cell', 'server_restart', 'not_ready', 'finish_failed'].includes(data.code)) crashSocket.emit('mines:state')
      if (data.code === 'items_not_available') refreshInventory()
      hapticNotify('error')
      toast.error(i18n.t(`mines.alerts.${data.code}`, { defaultValue: i18n.t('mines.alerts.generic') }))
      if (data.client_id) setState((s) => ({ ...s, busy: false }))
    }
    const onConnect = () => setState((s) => ({ ...s, connected: true, ready: false }))
    const onDisconnect = () => setState((s) => ({ ...s, connected: false, ready: false, busy: false }))
    const handlers = {
      'mines:state': snapshot,
      'mines:bet:ok': onBet,
      'mines:click:ok': onClick,
      'mines:result': onResult,
      'mines:feed': ({ games }) => setState((s) => ({ ...s, feed: [...(games || []), ...s.feed].filter((game, index, all) => all.findIndex((item) => item.uid === game.uid) === index).slice(0, 30) })),
      'mines:disabled': ({ disabled }) => setState((s) => ({ ...s, disabled: Boolean(disabled) })),
      'server:time': ({ serverTime }) => { if (Number.isFinite(Number(serverTime))) setState((s) => ({ ...s, clockOffset: Number(serverTime) - Date.now() })) },
      'user:inventory': () => { if (!isGuest && crashSocket.resolveAuthToken() === crashSocket.authToken) refreshInventory() },
      'user:alert': onAlert
    }
    Object.entries(handlers).forEach(([event, handler]) => crashSocket.on(event, handler))
    crashSocket.onConnect(onConnect)
    crashSocket.onDisconnect(onDisconnect)
    crashSocket.subscribe('mines')
    if (crashSocket.isConnected()) { onConnect(); crashSocket.emit('mines:state') }
    if (pendingRef.current) scheduleBetRecovery(pendingRef.current)
    const onVisible = () => { if (document.visibilityState === 'visible') { crashSocket.connect(); crashSocket.emit('mines:state') } }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      active = false
      if (!isGuest && (gameRef.current || pendingRef.current || actionRef.current)) {
        retainedGames.set(userId, { game: gameRef.current, pending: pendingRef.current, action: actionRef.current })
      } else retainedGames.delete(userId)
      clearTimeout(betTimerRef.current)
      clearTimeout(actionTimerRef.current)
      clearTimeout(recoveryTimerRef.current)
      document.removeEventListener('visibilitychange', onVisible)
      Object.entries(handlers).forEach(([event, handler]) => crashSocket.off(event, handler))
      crashSocket.offConnect(onConnect)
      crashSocket.offDisconnect(onDisconnect)
      crashSocket.unsubscribe('mines')
    }
  }, [isGuest, userId, refreshInventory, playSound, scheduleBetRecovery])

  const bet = (mines, crystal, items) => {
    const s = stateRef.current
    if (isGuest) { useAppStore.getState().openLogin(); return false }
    if (!s.connected || !s.ready || s.disabled || s.game || s.busy || pendingRef.current) return false
    const clientId = crypto.randomUUID().replaceAll('-', '').slice(0, 32)
    pendingRef.current = clientId
    setState((prev) => ({ ...prev, busy: true }))
    if (!crashSocket.emit('mines:bet', { mines, crystal, items, client_id: clientId })) { pendingRef.current = null; setState((prev) => ({ ...prev, busy: false })); return false }
    scheduleBetRecovery(clientId)
    return true
  }
  const act = (cell) => {
    const s = stateRef.current
    const game = gameRef.current
    if (!game || !s.ready || !s.connected || s.busy || actionRef.current || recoveryRef.current || game.finishing || (game.step > 0 && !game.next_multiplier)) return
    actionRef.current = { uid: game.uid, step: game.step + 1, cell }
    setState((prev) => ({ ...prev, busy: true }))
    if (!crashSocket.emit('mines:click', { uid: game.uid, step: game.step + 1, cell })) {
      actionRef.current = null
      setState((prev) => ({ ...prev, busy: false, ready: false }))
      return
    }
    clearTimeout(actionTimerRef.current)
    actionTimerRef.current = setTimeout(() => { if (actionRef.current?.cell === cell) crashSocket.emit('mines:state') }, 3000)
  }
  const cashout = () => {
    const s = stateRef.current
    const game = gameRef.current
    if (!game || game.step < 1 || !game.next_multiplier || !s.ready || !s.connected || s.busy || actionRef.current || recoveryRef.current || game.finishing) return
    actionRef.current = { uid: game.uid, cashout: true }
    setState((prev) => ({ ...prev, busy: true }))
    if (!crashSocket.emit('mines:cashout', { uid: game.uid })) {
      actionRef.current = null
      setState((prev) => ({ ...prev, busy: false, ready: false }))
      return
    }
    clearTimeout(actionTimerRef.current)
    actionTimerRef.current = setTimeout(() => { if (actionRef.current?.cashout) crashSocket.emit('mines:state') }, 3000)
  }

  return { ...state, bet, act, cashout, refreshInventory }
}
